import { useCallback, useMemo } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import { ActiveSipProfile, PhoneSyncApi } from '@frontend/api-phone-sync';
import {
  DeviceSelectModal,
  PhoneSyncModal,
  usePhonePairSuccessHandler,
  usePhoneSyncControl,
} from '@frontend/phone-sync';
import { useAppScopeStore } from '@frontend/scope';
import { ModalControlModalProps, ModalControlTriggerProps, useModalControl } from '@frontend/design-system';
import { useDialpadWidgetControl } from './dialpad-widget-control-provider';

type DialpadEstablishConnectionContextValue = {
  pairDeviceModalProps: ModalControlModalProps;
  pairDeviceTriggerProps: ModalControlTriggerProps;
  connectDeviceModalProps: ModalControlModalProps;
  connectDeviceTriggerProps: ModalControlTriggerProps;
  phoneSyncButtonProps: ReturnType<typeof usePhoneSyncControl>['buttonProps'];
  phoneSyncModalProps: ReturnType<typeof usePhoneSyncControl>['modalProps'];
  deviceSelectModalProps: ModalControlModalProps;
  deviceSelectTriggerProps: ModalControlTriggerProps;
};

const DialpadEstablishConnectionContext = createContext({} as DialpadEstablishConnectionContextValue);

type DialpadConnectProviderProps = {
  children: React.ReactNode;
};

export const DialpadConnectProvider = ({ children }: DialpadConnectProviderProps) => {
  const { selectedLocationIds } = useAppScopeStore();
  const open = useDialpadWidgetControl((ctx) => ctx.open);
  const { modalProps: pairDeviceModalProps, triggerProps: pairDeviceTriggerProps } = useModalControl();
  const { modalProps: connectDeviceModalProps, triggerProps: connectDeviceTriggerProps } = useModalControl();
  const deviceSelectModal = useModalControl();
  const { buttonProps: manualPairProps, modalProps: phoneSyncModalProps } = usePhoneSyncControl();

  const getHandler = usePhonePairSuccessHandler();
  const onSyncSuccess = useMemo(() => getHandler(selectedLocationIds), [getHandler, selectedLocationIds]);

  const handleSyncSuccess = useCallback(
    async (activeSipProfile: ActiveSipProfile) => {
      await onSyncSuccess(activeSipProfile);
      open?.();
    },
    [onSyncSuccess, open]
  );

  const handleConnectionSuccessCallback = useCallback(() => {
    connectDeviceModalProps.onClose();
    open?.();
  }, [connectDeviceModalProps, open]);

  const value = useMemo(
    () => ({
      pairDeviceModalProps,
      pairDeviceTriggerProps,
      connectDeviceModalProps,
      connectDeviceTriggerProps,
      phoneSyncButtonProps: manualPairProps,
      phoneSyncModalProps,
      deviceSelectModalProps: deviceSelectModal.modalProps,
      deviceSelectTriggerProps: deviceSelectModal.triggerProps,
    }),
    [
      pairDeviceModalProps,
      pairDeviceTriggerProps,
      connectDeviceModalProps,
      connectDeviceTriggerProps,
      manualPairProps,
      phoneSyncModalProps,
      deviceSelectModal.modalProps,
      deviceSelectModal.triggerProps,
    ]
  );

  return (
    <DialpadEstablishConnectionContext.Provider value={value}>
      {children}
      <DeviceSelectModal
        variant='dialpad'
        deviceSelectModal={deviceSelectModal}
        closeModal={handleConnectionSuccessCallback}
        locationIds={selectedLocationIds}
      />
      <PhoneSyncModal
        {...phoneSyncModalProps}
        locationIds={selectedLocationIds}
        onSyncSuccess={handleSyncSuccess}
        syncFn={PhoneSyncApi.pairDevice}
      />
    </DialpadEstablishConnectionContext.Provider>
  );
};

export const useDialpadEstablishConnectionControl = <T extends any>(
  selector: (value: DialpadEstablishConnectionContextValue) => T
) => {
  return useContextSelector(DialpadEstablishConnectionContext, selector);
};
